/**
 * Theme runtime — the ONLY place that touches the DOM for theming.
 *
 * Subscribes once to the theme store at module load. On any relevant
 * change it rewrites a single <style id="alive-theme"> tag in <head>
 * containing the resolved CSS variables, and toggles the `dark` class
 * + the active `status-theme-*` class on <html>.
 *
 * Why a <style> tag instead of `documentElement.style.setProperty`?
 *
 *   - Inline styles on <html> are fragile: any other code that touches
 *     `root.style.*` (font size, colorScheme, third-party widgets) can
 *     stomp them, and HMR can clear them on reload.
 *   - A <style> tag appended last in <head> wins over Tailwind's
 *     `@layer base` :root rule via stylesheet ordering, while still
 *     being subordinate to anything *legitimately* set inline.
 *   - One singleton element means there is no chance of duplicates and
 *     no race between multiple writers.
 */

import { useAgentStore } from '../store/agentStore'
import { STATUS_THEMES, STATUS_THEME_CLASSES } from './statusThemes'
import {
  FONT_SIZE_SCALES,
  resolveIsDark,
  useThemeStore,
  type FontSize,
  type ThemeMode,
} from './themeStore'
import type { AgentStatus } from '../types'

const STYLE_TAG_ID = 'alive-theme'

/** CSS variables this runtime owns. Listed so we can scrub legacy
 *  inline values from earlier implementations on first run. */
const OWNED_VARS = [
  '--color-primary',
  '--color-primary-light',
  '--color-primary-dark',
  '--color-primary-soft',
  '--material-glow',
  '--shadow-button',
  '--font-scale',
] as const

interface ResolvedTheme {
  isDark: boolean
  status: AgentStatus | 'none'
  fontSize: FontSize
}

const resolve = (
  themeMode: ThemeMode,
  statusThemeEnabled: boolean,
  agentStatus: AgentStatus | 'none',
  fontSize: FontSize,
): ResolvedTheme => ({
  isDark: resolveIsDark(themeMode),
  status: statusThemeEnabled ? agentStatus : 'none',
  fontSize,
})

const buildCss = (resolved: ResolvedTheme): string => {
  const palette = STATUS_THEMES[resolved.status]
  const scale = FONT_SIZE_SCALES[resolved.fontSize]
  return (
    ':root{' +
    `--color-primary:${palette.accent};` +
    `--color-primary-light:${palette.accentLight};` +
    `--color-primary-dark:${palette.accentDark};` +
    `--color-primary-soft:${palette.accentSoft};` +
    `--material-glow:${palette.glow};` +
    `--shadow-button:${palette.buttonShadow};` +
    `--font-scale:${scale};` +
    '}' +
    `html{font-size:${scale * 16}px;}`
  )
}

const ensureStyleTag = (): HTMLStyleElement => {
  let el = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_TAG_ID
    document.head.appendChild(el)
    return el
  }
  // Keep it as the last <head> child so :root rules win deterministically.
  if (el.parentNode !== document.head || el !== document.head.lastElementChild) {
    document.head.appendChild(el)
  }
  return el
}

const scrubLegacyInlineVars = (root: HTMLElement) => {
  for (const name of OWNED_VARS) {
    if (root.style.getPropertyValue(name)) {
      root.style.removeProperty(name)
    }
  }
  // Earlier code also wrote `root.style.fontSize` and `colorScheme`.
  // Clear fontSize so the stylesheet rule wins; colorScheme is reset
  // explicitly below in `applyResolvedTheme`.
  if (root.style.fontSize) root.style.fontSize = ''
}

const applyResolvedTheme = (resolved: ResolvedTheme) => {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  // TEMP DIAGNOSTIC — remove once the theme bug is closed.
  // Logs every theme application so we can see exactly when and why
  // the runtime decides to flip dark mode on.
  // eslint-disable-next-line no-console
  console.log('[theme] apply', {
    resolved,
    storeThemeMode: useThemeStore.getState().themeMode,
    htmlClassBefore: root.className,
    stack: new Error().stack?.split('\n').slice(2, 6).join('\n'),
  })

  // Style tag → CSS variables + html font-size.
  const styleEl = ensureStyleTag()
  const css = buildCss(resolved)
  if (styleEl.textContent !== css) {
    styleEl.textContent = css
  }

  // Dark class + colorScheme on <html>.
  root.classList.toggle('dark', resolved.isDark)
  root.style.colorScheme = resolved.isDark ? 'dark' : 'light'

  // Status theme class — clear all, set one.
  const targetClass = STATUS_THEMES[resolved.status].className
  for (const cls of STATUS_THEME_CLASSES) {
    if (cls !== targetClass) root.classList.remove(cls)
  }
  if (!root.classList.contains(targetClass)) {
    root.classList.add(targetClass)
  }
}

/** Read the current store state and apply it. */
const applyFromStore = () => {
  const { themeMode, statusThemeEnabled, agentStatus, fontSize } =
    useThemeStore.getState()
  applyResolvedTheme(
    resolve(themeMode, statusThemeEnabled, agentStatus, fontSize),
  )
}

let started = false

/**
 * Boot the theme runtime. Idempotent: safe to call from any entry point
 * (main.tsx, tests, story setups). The first call wires up subscriptions
 * and performs the initial DOM application; subsequent calls are no-ops.
 */
export const startThemeRuntime = (): void => {
  if (started || typeof document === 'undefined') return
  started = true

  scrubLegacyInlineVars(document.documentElement)

  // 1. Apply once with whatever the persisted store currently holds.
  applyFromStore()

  // 2. React to any store change.
  useThemeStore.subscribe(() => applyFromStore())

  // 3. Bridge agent status from the agent store into the theme store.
  //    The previous implementation had a React hook compute this and
  //    forgot to re-run on route changes; doing it via a vanilla
  //    subscription means it stays correct regardless of routing.
  const computeAgentStatus = (): AgentStatus | 'none' => {
    const { myAgents, primaryAgentId } = useAgentStore.getState()
    const agent =
      myAgents.find((a) => a.id === primaryAgentId) ?? myAgents[0] ?? null
    return agent ? agent.status : 'none'
  }
  // Initial sync, then subscribe.
  useThemeStore.getState().setAgentStatus(computeAgentStatus())
  useAgentStore.subscribe(() => {
    const next = computeAgentStatus()
    if (next !== useThemeStore.getState().agentStatus) {
      useThemeStore.getState().setAgentStatus(next)
    }
  })

  // 4. Follow OS dark-mode changes when themeMode === 'system'.
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      if (useThemeStore.getState().themeMode === 'system') applyFromStore()
    }
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
    } else if (typeof (mq as MediaQueryList).addListener === 'function') {
      // Safari < 14 fallback.
      ;(mq as MediaQueryList).addListener(onChange)
    }
  }
}
